import { useMemo, useState } from 'react';
import type { ColumnSchema, ColumnType, Row } from '../lib/types';

interface AnomalyGridProps {
  columns: ColumnSchema[];
  headers: string[];
  rows: Row[];
  overrides: Record<string, ColumnType>;
  onOverride: (name: string, type: ColumnType) => void;
}

const TYPES: ColumnType[] = ['string', 'int', 'float', 'boolean', 'iso-date', 'email', 'uuid', 'url'];
const PAGE_SIZE = 100;

const TYPE_BADGE: Record<string, string> = {
  string: 'bg-slate-700/60 text-slate-300',
  int: 'bg-cyan-500/15 text-cyan-300',
  float: 'bg-cyan-500/15 text-cyan-300',
  boolean: 'bg-amber-500/15 text-amber-300',
  'iso-date': 'bg-lime-500/15 text-lime-300',
  email: 'bg-violet-500/15 text-violet-300',
  uuid: 'bg-violet-500/15 text-violet-300',
  url: 'bg-violet-500/15 text-violet-300',
};

export function AnomalyGrid({ columns, headers, rows, overrides, onOverride }: AnomalyGridProps) {
  const [page, setPage] = useState(0);
  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const start = Math.min(page, pageCount - 1) * PAGE_SIZE;

  const anomalyRows = useMemo(() => {
    const sets = columns.map((col) => new Set(col.anomalies.map((a) => a.row)));
    return sets;
  }, [columns]);

  const visible = rows.slice(start, start + PAGE_SIZE);

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900/50 p-4 shadow-panel sm:p-5">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-sm font-semibold text-slate-100">Anomaly grid</h2>
          <p className="mt-0.5 text-xs text-slate-500">
            Cells breaking the column&apos;s majority type are flagged in red. Override a type when you know better.
          </p>
        </div>
        {pageCount > 1 && (
          <div className="flex items-center gap-1.5 text-xs text-slate-500">
            <button
              type="button"
              disabled={start === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              className="rounded border border-slate-700 px-2 py-1 transition hover:border-slate-500 disabled:opacity-40"
            >
              ‹ prev
            </button>
            <span className="font-mono">
              {Math.floor(start / PAGE_SIZE) + 1}/{pageCount}
            </span>
            <button
              type="button"
              disabled={start + PAGE_SIZE >= rows.length}
              onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
              className="rounded border border-slate-700 px-2 py-1 transition hover:border-slate-500 disabled:opacity-40"
            >
              next ›
            </button>
          </div>
        )}
      </div>

      <div className="scrollbar-thin mt-3 max-h-[420px] overflow-auto rounded-xl border border-slate-800">
        <table className="w-full border-collapse font-mono text-[11.5px]">
          <thead className="sticky top-0 z-10 bg-slate-900">
            <tr>
              <th className="w-12 border-b border-slate-800 px-2 py-2 text-right text-[10px] uppercase text-slate-500">#</th>
              {columns.map((col, c) => (
                <th key={col.name} className="min-w-[140px] border-b border-l border-slate-800 px-2 py-2 text-left align-top">
                  <div className="font-sans text-xs font-semibold text-slate-200">{headers[c] ?? col.name}</div>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${TYPE_BADGE[col.type] ?? TYPE_BADGE.string}`}>
                      {overrides[col.name] ?? col.type}
                    </span>
                    {col.anomalyCount > 0 && (
                      <span className="rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-rose-300">
                        {col.anomalyCount} ✗
                      </span>
                    )}
                    {col.nullCount > 0 && (
                      <span className="rounded bg-slate-800 px-1.5 py-0.5 text-[10px] text-slate-500">
                        {col.nullCount} null
                      </span>
                    )}
                  </div>
                  <select
                    aria-label={`Override type for ${col.name}`}
                    value={overrides[col.name] ?? col.type}
                    onChange={(event) => onOverride(col.name, event.target.value as ColumnType)}
                    className="mt-1 w-full rounded border border-slate-800 bg-slate-950 px-1 py-0.5 text-[10px] text-slate-400 outline-none focus:border-amber-500"
                  >
                    {TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row, r) => {
              const rowNum = start + r + 1; // 1-based data row, matches anomaly indexing
              return (
                <tr key={start + r} className="even:bg-slate-900/40">
                  <td className="border-b border-slate-800/60 px-2 py-1 text-right text-slate-600">{rowNum}</td>
                  {columns.map((col, c) => {
                    const value = row[c] ?? null;
                    const bad = anomalyRows[c]?.has(rowNum) ?? false;
                    return (
                      <td
                        key={col.name}
                        title={bad ? `expected ${col.type}` : undefined}
                        className={`max-w-[220px] truncate border-b border-l border-slate-800/60 px-2 py-1 ${
                          value === null
                            ? 'italic text-slate-600'
                            : bad
                              ? 'bg-rose-500/15 font-semibold text-rose-300'
                              : 'text-slate-300'
                        }`}
                      >
                        {value === null ? 'null' : value}
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-2 text-[11px] text-slate-500">
        Rows {rows.length === 0 ? 0 : start + 1}–{Math.min(start + PAGE_SIZE, rows.length)} of {rows.length.toLocaleString()}.
      </p>
    </section>
  );
}
